'use client';

import Link from 'next/link';
import Image from 'next/image';

export default function TributePage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 relative overflow-hidden">
      {/* Elegant ambient background */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="ambient-orb orb-1"></div>
        <div className="ambient-orb orb-2"></div>
        <div className="ambient-orb orb-3"></div>
        <div className="subtle-grid"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Header */}
        <div className="text-center mb-16 animate-fade-in">
          <h1 className="text-5xl md:text-7xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 mb-6 animate-gradient">
            A Tribute to Elon Musk
          </h1>
          <p className="text-2xl md:text-3xl text-gray-300 italic">
            The Visionary Behind Free Knowledge
          </p>
        </div>

        {/* Main Content */}
        <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
          {/* Image Section */}
          <div className="flex justify-center">
            <div className="relative group">
              <div className="absolute -inset-1 bg-gradient-to-br from-blue-500/30 via-purple-500/30 to-indigo-500/30 rounded-lg blur-xl opacity-60 group-hover:opacity-80 transition duration-700"></div>
              <div className="relative ring-1 ring-white/10 rounded-lg overflow-hidden">
                <Image 
                  src="https://upload.wikimedia.org/wikipedia/commons/3/34/Elon_Musk_Royal_Society_%28crop2%29.jpg"
                  alt="Elon Musk"
                  className="rounded-lg shadow-2xl w-full h-auto transition-transform duration-500 group-hover:scale-[1.02]"
                  width={600}
                  height={800}
                />
              </div>
            </div>
          </div>

          {/* Text Section */}
          <div className="text-gray-200 space-y-6">
            <div className="bg-white/10 backdrop-blur-lg rounded-xl p-8 border border-white/20 shadow-2xl hover:bg-white/[0.13] hover:border-white/30 transition-all duration-500">
              <h2 className="text-3xl font-bold text-blue-400 mb-4 flex items-center">
                <span className="text-4xl mr-3">🚀</span>
                The Inspiration
              </h2>
              <p className="text-lg leading-relaxed">
                Elon Musk stands as the cornerstone inspiration for Grokypedia. His unwavering commitment to 
                <span className="text-purple-400 font-semibold"> transparency</span>, 
                <span className="text-blue-400 font-semibold"> free speech</span>, and 
                <span className="text-pink-400 font-semibold"> democratized knowledge</span> has paved the way 
                for this revolutionary platform.
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-lg rounded-xl p-8 border border-white/20 shadow-2xl hover:bg-white/[0.13] hover:border-white/30 transition-all duration-500">
              <h2 className="text-3xl font-bold text-purple-400 mb-4 flex items-center">
                <span className="text-4xl mr-3">💡</span>
                Vision of Freedom
              </h2>
              <p className="text-lg leading-relaxed">
                Through his groundbreaking work with <span className="font-semibold">Grok AI</span> and 
                <span className="font-semibold"> X (formerly Twitter)</span>, Elon has championed the cause of 
                information freedom. His vision challenges the status quo of biased, controlled narratives 
                and empowers humanity with unfiltered truth.
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-lg rounded-xl p-8 border border-white/20 shadow-2xl hover:bg-white/[0.13] hover:border-white/30 transition-all duration-500">
              <h2 className="text-3xl font-bold text-pink-400 mb-4 flex items-center">
                <span className="text-4xl mr-3">🌍</span>
                Thanks to Elon
              </h2>
              <p className="text-lg leading-relaxed">
                This project exists <span className="text-yellow-400 font-bold">because of him</span>. 
                His relentless pursuit of innovation, his courage to challenge established systems, 
                and his dedication to making information accessible to all have inspired Grokypedia&apos;s creation.
              </p>
            </div>
          </div>
        </div>

        {/* Quote Section */}
        <div className="mb-16">
          <div className="bg-gradient-to-r from-blue-600/20 via-purple-600/20 to-pink-600/20 backdrop-blur-lg rounded-2xl p-12 border border-white/30 shadow-2xl">
            <div className="text-center">
              <p className="text-3xl md:text-4xl font-serif italic text-gray-100 mb-6 leading-relaxed">
                &ldquo;The goal of Grokypedia is to be maximally truth-seeking, 
                even if that truth is unpopular.&rdquo;
              </p>
              <p className="text-xl text-gray-400">— Inspired by Elon Musk&apos;s Philosophy</p>
            </div>
          </div>
        </div>

        {/* Principles Section */}
        <div className="mb-16">
          <h2 className="text-4xl font-bold text-center text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400 mb-12">
            Core Principles We Share
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20 hover:border-blue-400/50 transition-all duration-500 hover:transform hover:scale-[1.02] hover:shadow-lg hover:shadow-blue-500/20">
              <div className="text-5xl mb-4 text-center">🔓</div>
              <h3 className="text-2xl font-bold text-blue-400 mb-3 text-center">Free Access</h3>
              <p className="text-gray-300 text-center">
                Knowledge should be free and accessible to everyone, without paywalls or restrictions.
              </p>
            </div>
            
            <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20 hover:border-purple-400/50 transition-all duration-500 hover:transform hover:scale-[1.02] hover:shadow-lg hover:shadow-purple-500/20">
              <div className="text-5xl mb-4 text-center">⚖️</div>
              <h3 className="text-2xl font-bold text-purple-400 mb-3 text-center">Neutrality</h3>
              <p className="text-gray-300 text-center">
                Information without bias, where all perspectives can be heard and evaluated fairly.
              </p>
            </div>
            
            <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20 hover:border-pink-400/50 transition-all duration-500 hover:transform hover:scale-[1.02] hover:shadow-lg hover:shadow-pink-500/20">
              <div className="text-5xl mb-4 text-center">🤖</div>
              <h3 className="text-2xl font-bold text-pink-400 mb-3 text-center">AI-Powered</h3>
              <p className="text-gray-300 text-center">
                Leveraging cutting-edge AI to enhance, verify, and democratize knowledge creation.
              </p>
            </div>
          </div>
        </div>

        {/* Community Voices */}
        <div className="mb-16">
          <h2 className="text-4xl font-bold text-center text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400 mb-12">
            Community Voices
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white/5 backdrop-blur-lg rounded-xl p-6 border border-white/10 hover:bg-white/10 transition-all duration-300">
              <p className="text-gray-300 mb-4 italic">
                &ldquo;Elon Musk should drop Grokypedia just to see Wikipedia panic.&rdquo;
              </p>
              <p className="text-blue-400 font-semibold">— @cb_doge</p>
            </div>
            
            <div className="bg-white/5 backdrop-blur-lg rounded-xl p-6 border border-white/10 hover:bg-white/10 transition-all duration-300">
              <p className="text-gray-300 mb-4 italic">
                &ldquo;It&apos;s time to replace the woke Wikipedia with Grokypedia.&rdquo;
              </p>
              <p className="text-purple-400 font-semibold">— DogeDesigner</p>
            </div>
            
            <div className="bg-white/5 backdrop-blur-lg rounded-xl p-6 border border-white/10 hover:bg-white/10 transition-all duration-300">
              <p className="text-gray-300 mb-4 italic">
                &ldquo;Elon Musk&apos;s Grokypedia, powered by xAI&apos;s Grok, promises to eclipse Wikipedia by dismantling the pervasive left-wing bias.&rdquo;
              </p>
              <p className="text-pink-400 font-semibold">— @DefiantLs</p>
            </div>
            
            <div className="bg-white/5 backdrop-blur-lg rounded-xl p-6 border border-white/10 hover:bg-white/10 transition-all duration-300">
              <p className="text-gray-300 mb-4 italic">
                &ldquo;Elon Musk baut an einer Alternative zum linkslastigen Wikipedia.&rdquo;
              </p>
              <p className="text-blue-400 font-semibold">— Roland Tichy</p>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="text-center">
          <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 rounded-2xl p-12 shadow-2xl">
            <h2 className="text-4xl font-bold text-white mb-6">
              Join the Revolution
            </h2>
            <p className="text-xl text-gray-100 mb-8 max-w-3xl mx-auto">
              Inspired by Elon&apos;s vision, we&apos;re building a knowledge platform that prioritizes truth, 
              freedom, and accessibility. Be part of the change.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                href="/contribute"
                className="bg-white text-purple-600 px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition-all duration-300 transform hover:scale-105 shadow-lg"
              >
                Start Contributing
              </Link>
              <Link 
                href="/"
                className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-white/10 transition-all duration-300 transform hover:scale-105"
              >
                Explore Grokypedia
              </Link>
            </div>
          </div>
        </div>

        {/* Footer Message */}
        <div className="text-center mt-16">
          <p className="text-gray-400 text-lg">
            <span className="text-yellow-400">✨</span> Thank you, Elon, for inspiring a future of free knowledge <span className="text-yellow-400">✨</span>
          </p>
        </div>
      </div>

      {/* CSS for animations */}
      <style jsx>{`
        @keyframes gradient {
          0% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
          100% {
            background-position: 0% 50%;
          }
        }

        .animate-gradient {
          background-size: 200% 200%;
          animation: gradient 4s ease infinite;
        }

        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fade-in {
          animation: fade-in 1.2s ease-out;
        }

        @keyframes tilt {
          0%, 100% {
            transform: rotate(-0.5deg);
          }
          50% {
            transform: rotate(0.5deg);
          }
        }

        .animate-tilt {
          animation: tilt 4s ease-in-out infinite;
        }

        /* Elegant ambient orbs */
        .ambient-orb {
          position: absolute;
          border-radius: 50%;
          filter: blur(120px);
          opacity: 0.15;
          animation: float 20s ease-in-out infinite;
        }

        .orb-1 {
          width: 600px;
          height: 600px;
          background: linear-gradient(135deg, #4f46e5, #7c3aed);
          top: -200px;
          left: -200px;
          animation-delay: 0s;
        }

        .orb-2 {
          width: 500px;
          height: 500px;
          background: linear-gradient(135deg, #2563eb, #4f46e5);
          bottom: -150px;
          right: -150px;
          animation-delay: 5s;
        }

        .orb-3 {
          width: 400px;
          height: 400px;
          background: linear-gradient(135deg, #7c3aed, #ec4899);
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          animation-delay: 10s;
        }

        @keyframes float {
          0%, 100% {
            transform: translate(0, 0) scale(1);
          }
          33% {
            transform: translate(50px, -30px) scale(1.1);
          }
          66% {
            transform: translate(-30px, 50px) scale(0.9);
          }
        }

        /* Subtle grid overlay */
        .subtle-grid {
          position: absolute;
          inset: 0;
          background-image: 
            linear-gradient(rgba(99, 102, 241, 0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(99, 102, 241, 0.03) 1px, transparent 1px);
          background-size: 50px 50px;
          animation: grid-move 60s linear infinite;
        }

        @keyframes grid-move {
          0% {
            transform: translate(0, 0);
          }
          100% {
            transform: translate(50px, 50px);
          }
        }
      `}</style>
    </div>
  );
}

